/* eslint-disable import/prefer-default-export */
import { rounding } from 'utils/widgetUtil'
import InsightViewOption, { InsightViewOptionStrings } from 'enums/InsightViewOption'
import CountType, { CountTypeStrings } from 'enums/CountType'
import messageUtil from 'utils/messageUtil'
import TableSortOption, { TableSortOptionStrings } from 'enums/TableSortOption'
import { ColSortingConfigs } from 'components/visualization/Table/types'
import { getTwEmoji } from 'utils/emojiUtil'

import { MAX_INDEX } from '../constants'

export const getPostsPercentage = (share: number) => {
  const sharePercentage = share * 100
  return sharePercentage
}

export const getDisplayPostPercentage = (share: number) => {
  if (share < 0.01 && share !== 0) {
    return '<1%'
  }
  const postPercentage = getPostsPercentage(share)
  return `${rounding(postPercentage, 0)}%`
}

export const getIndexValue = (index: number) =>
  index < 0 || index >= MAX_INDEX ? MAX_INDEX : index

export const getDisplayIndex = (index: number) => {
  const indexValue = getIndexValue(index)
  const numberFormatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })

  return indexValue === MAX_INDEX
    ? `${indexValue}+`
    : numberFormatter.format(rounding(indexValue, 2))
}

export const getPercentByIndex = (index: number, maxIndex: number) => {
  const indexValue = getIndexValue(index)
  const indexPercentage = (indexValue * 100) / maxIndex
  const displayIndex = getDisplayIndex(index)
  if (displayIndex === '0.00') return 0
  return indexPercentage
}

export const getLabelByViewOption = (name: string, viewOption: InsightViewOptionStrings) => {
  switch (viewOption) {
    case InsightViewOption.PROFESSIONS:
      return messageUtil.professionsLabel(name) || name
    case InsightViewOption.INTERESTS:
      return messageUtil.interestsLabel(name) || name
    case InsightViewOption.ETHNICITY:
      return messageUtil.ethnicityLabel(name) || name
    case InsightViewOption.BIOTEXT:
      return getTwEmoji(name) || name
    default:
      return name
  }
}

export const getBarColor = ({
  countType,
  viewOption,
  name
}: {
  countType: CountTypeStrings
  viewOption: InsightViewOptionStrings
  name?: string
}) => {
  if (viewOption === InsightViewOption.MALE_FEMALE) {
    return name === 'Female' ? 'semantic.female' : 'semantic.male'
  }
  return countType === CountType.POSTS ? 'metric.mainMetrics.post' : 'metric.mainMetrics.authors'
}

export const getSortingConfigByTableSortOption = (
  tableSortOption: TableSortOptionStrings
): ColSortingConfigs<'name' | 'share' | 'index'> => {
  switch (tableSortOption) {
    case TableSortOption.ALPHABET_ASC:
      return {
        field: 'name',
        order: TableSortOption.ASCEND
      }
    case TableSortOption.ALPHABET_DSC:
      return {
        field: 'name',
        order: TableSortOption.DESCEND
      }
    case TableSortOption.VALUE_ASC:
      return {
        field: 'share',
        order: TableSortOption.ASCEND
      }
    case TableSortOption.VALUE_DSC:
      return {
        field: 'share',
        order: TableSortOption.DESCEND
      }
    case TableSortOption.INDEX_ASC:
      return {
        field: 'index',
        order: TableSortOption.ASCEND
      }
    case TableSortOption.INDEX_DSC:
    default:
      return {
        field: 'index',
        order: TableSortOption.DESCEND
      }
  }
}
